import React, { useState, useEffect, useRef, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import {
  getFirestore,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { auth } from '../../../firebase';
import './investment.css';
import {
  BarChart3,
  ShieldCheck,
  Coins,
  Gift,
  Trophy,
  MessageSquare,
  Send,
  X,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Clock,
  Calendar,
  Lock,
  Unlock,
  TrendingUp
} from 'lucide-react';
import { API_BASE } from '../../../config/api';

const db = getFirestore();

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');

  if (!token) {
    throw new Error('No authentication token found');
  }

  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const Investments = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkInInvestment, setCheckInInvestment] = useState(null);
  const [checkInCode, setCheckInCode] = useState('');
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkInError, setCheckInError] = useState('');
  const [lastCheckInResult, setLastCheckInResult] = useState(null);

  const [investments, setInvestments] = useState([]);
  const [investmentSummary, setInvestmentSummary] = useState({
    totalInvested: 0,
    totalPotentialReturn: 0,
    completedCount: 0,
    activeCount: 0,
    investmentCount: 0,
    lockedInvestment: 0
  });

  const [financials, setFinancials] = useState({
    totalPortfolio: 0,
    netProfitLoss: 0,
    availableLiquidity: 0,
    lockedInvestment: 0
  });

  const [allocationData, setAllocationData] = useState({
    stocks: 0,
    bonds: 0,
    commodities: 0
  });

  const chatEndRef = useRef(null);

  const safeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatMoney = (value) => {
    const num = safeNumber(value);

    return `$${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const parseDate = (value) => {
    if (!value) return null;

    if (typeof value?.toDate === 'function') {
      return value.toDate();
    }

    if (value?.seconds) {
      return new Date(value.seconds * 1000);
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  };

  const DAILY_TRADING_STATUSES = [
    'active',
    'auto_renewed',
    'running',
    'in_progress',
    'pending',
    'trading'
  ];

  const isDailyTradingInvestment = (investment) => {
    if (!investment) return false;

    if (investment.interestStatus === 'claimed') {
      return false;
    }

    const completedDays = safeNumber(investment.completedDays);

    if (completedDays >= 30) {
      return false;
    }

    const status = String(investment.status || '').toLowerCase();

    if (status && !DAILY_TRADING_STATUSES.includes(status)) {
      return false;
    }

    return true;
  };

  const fetchAllData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();

      console.log(' Fetching fresh data...');
      
      // ✅ Cache buster to ensure we get fresh data from the backend
      const timestamp = new Date().getTime();

      const metricsResponse = await axios.get(
        `${API_BASE}/api/identity/dashboard-metrics?t=${timestamp}`,
        { headers }
      );

      if (metricsResponse?.data?.success) {
        console.log('✅ Metrics received:', metricsResponse.data.balances);
        setFinancials(prev => ({
          ...prev,
          ...(metricsResponse.data.balances || {})
        }));

        setAllocationData(prev => ({
          ...prev,
          ...(metricsResponse.data.allocations || {})
        }));
      }

      console.log('📊 Fetching investments...');
      const investmentsResponse = await axios.get(
        `${API_BASE}/api/investment/user-investments?t=${timestamp}`,
        { headers }
      );

      if (investmentsResponse?.data?.success) {
        const safeInvestments = Array.isArray(investmentsResponse.data.investments)
          ? investmentsResponse.data.investments
          : [];

        console.log('✅ Investments fetched:', safeInvestments.length);
        safeInvestments.forEach(inv => {
          console.log(`   - ${inv.symbol}: Principal $${inv.amount}, Current Value $${inv.currentValue}, Days: ${inv.completedDays}, Status: ${inv.status}`);
        });

        setInvestments(safeInvestments);

        setInvestmentSummary(prev => ({
          ...prev,
          ...(investmentsResponse.data.summary || {})
        }));
      } else {
        console.error('❌ Investments response not successful:', investmentsResponse.data);
      }
    } catch (err) {
      console.error('❌ Error fetching data:', err);

      if (err.response?.status === 401) {
        console.error('❌ Session expired. Redirecting to login...');
        localStorage.clear();

        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    } finally {
      setLoadingMetrics(false);
    }
  }, []);

  // ✅ FIXED: Check for JWT token FIRST, fallback to Firebase
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    const email = localStorage.getItem('userEmail');

    if (token) {
      console.log('✅ JWT token found, initializing user...');
      setCurrentUser({
        uid: 'jwt-user',
        name: email ? email.split('@')[0] : 'User',
        email: email || 'user@example.com',
        photo: null
      });
      fetchAllData();
    } else {
      console.log('⚠️ No JWT token, checking Firebase auth...');
      const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
        if (user) {
          setCurrentUser({
            uid: user.uid,
            name: user.displayName || user.email.split('@')[0],
            email: user.email,
            photo: user.photoURL || null
          });
          await fetchAllData();
        } else {
          setCurrentUser(null);
          setLoadingMetrics(false);
        }
      });
      return () => unsubscribeAuth();
    }
  }, [fetchAllData]);

  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      fetchAllData();
    }, 15000); // Refresh every 15 seconds

    return () => clearInterval(interval);
  }, [currentUser, fetchAllData]);

  useEffect(() => {
    const chatQuery = query(
      collection(db, 'investment_chat'),
      orderBy('timestamp', 'asc'),
      limit(50)
    );

    const unsubscribeChat = onSnapshot(chatQuery, snapshot => {
      const liveMessages = [];

      snapshot.forEach(doc => {
        liveMessages.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setMessages(liveMessages);
    });

    return () => unsubscribeChat();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || !currentUser || sending) return;

    setSending(true);

    try {
      await addDoc(collection(db, 'investment_chat'), {
        text: newMessage,
        uid: currentUser.uid,
        userName: currentUser.name,
        userPhoto: currentUser.photo,
        timestamp: new Date()
      });

      setNewMessage('');
    } catch (error) {
      alert('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleOpenCheckInModal = investment => {
    setCheckInInvestment(investment);
    setCheckInCode('');
    setCheckInError('');
    setLastCheckInResult(null);
    setShowCheckInModal(true);
  };

  const handleDailyCheckIn = async () => {
    if (!checkInInvestment || !checkInCode) return;

    if (checkInCode.length !== 8) {
      setCheckInError('Please enter a valid 8-character code');
      return;
    }

    setCheckInLoading(true);
    setCheckInError('');

    try {
      const headers = getAuthHeaders();

      const response = await axios.post(
        `${API_BASE}/api/investment/check-in/${checkInInvestment.id}`,
        {
          code: checkInCode.toUpperCase()
        },
        { headers }
      );

      if (response?.data?.success) {
        if (response.data.rewardReleased) {
          alert(`🎉 ${response.data.message || 'Check-in successful'}`);
        } else {
          setLastCheckInResult(response.data);
        }

        if (!response.data.rewardReleased) {
          setTimeout(() => {
            setShowCheckInModal(false);
            setCheckInCode('');
            setCheckInInvestment(null);
            setLastCheckInResult(null);
          }, 3000);
        } else {
          setShowCheckInModal(false);
          setCheckInCode('');
          setCheckInInvestment(null);
          setLastCheckInResult(null);
        }

        if (response.data.rewardReleased && response.data.updatedBalances) {
          setFinancials(prev => ({
            ...prev,
            ...response.data.updatedBalances
          }));
        }

        await fetchAllData();
      } else {
        setCheckInError(response?.data?.message || 'Failed to check in');
      }
    } catch (err) {
      setCheckInError(err.response?.data?.message || 'Failed to check in');
    } finally {
      setCheckInLoading(false);
    }
  };

  const getAssetIcon = assetClass => {
    switch (assetClass) {
      case 'stocks':
        return <BarChart3 size={18} />;
      case 'bonds':
        return <ShieldCheck size={18} />;
      case 'commodities':
        return <Coins size={18} />;
      default:
        return <Coins size={18} />;
    }
  };

  const getHoursUntilNextCheckIn = investment => {
    const lastCheckInDate = parseDate(
      investment?.lastCheckInDate || investment?.investedAt
    );

    if (!lastCheckInDate) return 0;

    const hoursPassed =
      (Date.now() - lastCheckInDate.getTime()) / (1000 * 60 * 60);

    const hoursRemaining = 24 - hoursPassed;

    if (hoursRemaining <= 0) return 0;

    return Math.ceil(hoursRemaining);
  };

  const getStatusBadge = investment => {
    if (investment.interestStatus === 'claimed') {
      return (
        <span className="status-badge claimed">
          <CheckCircle2 size={12} /> Cycle {investment.cycleNumber || 1} Complete
        </span>
      );
    }

    const hoursUntilNextCheckIn = getHoursUntilNextCheckIn(investment);
    const canCheckInNow = hoursUntilNextCheckIn === 0;

    if (isDailyTradingInvestment(investment)) {
      if (canCheckInNow) {
        return <span className="status-badge ready">🔑 Ready to Check In!</span>;
      }

      return (
        <span className="status-badge active">
          <Clock size={12} /> Day {safeNumber(investment.completedDays)}/30
        </span>
      );
    }

    if (investment.status === 'auto_renewed') {
      return (
        <span
          className="status-badge"
          style={{
            background: 'rgba(16, 185, 129, 0.2)',
            color: '#10b981'
          }}
        >
          <RefreshCw size={12} /> Auto-Renewed
        </span>
      );
    }

    return (
      <span className="status-badge active">
        <Clock size={12} /> Day {safeNumber(investment.completedDays)}/30
      </span>
    );
  };

  const safeInvestments = Array.isArray(investments) ? investments : [];

  const dailyTradingInvestments = safeInvestments.filter(
    isDailyTradingInvestment
  );

  const getTotalProfitEarned = () => {
    return safeInvestments.reduce((sum, inv) => {
      const isCompleted =
        inv.interestStatus === 'claimed' || inv.status === 'auto_renewed';

      if (!isCompleted) return sum;

      return sum + safeNumber(inv.profitPaidOut || inv.interestAmount || 0);
    }, 0);
  };

  const portfolioStats = [
    {
      id: 1,
      label: 'Locked Investment',
      value: formatMoney(financials.lockedInvestment),
      change: '🔒 Perpetual',
      isPositive: true,
      icon: <Lock size={14} style={{ color: '#f59e0b' }} />
    },
    {
      id: 2,
      label: 'Total Profit Earned',
      value: formatMoney(getTotalProfitEarned()),
      change: `${safeNumber(investmentSummary.completedCount)} cycles`,
      isPositive: true,
      icon: <TrendingUp size={14} style={{ color: '#10b981' }} />
    },
    {
      id: 3,
      label: 'Available to Withdraw',
      value: formatMoney(financials.availableLiquidity),
      change: '✅ Withdrawable',
      isPositive: true,
      icon: <Unlock size={14} style={{ color: '#10b981' }} />
    }
  ];

  const modalCompletedDays = safeNumber(checkInInvestment?.completedDays);
  const modalPrincipal = safeNumber(checkInInvestment?.amount);
  const modalCurrentValue = safeNumber(
    checkInInvestment?.currentValue || checkInInvestment?.amount || 0
  );
  const modalTargetProfit = safeNumber(checkInInvestment?.interestAmount);
  const modalProfitPaidOut = safeNumber(checkInInvestment?.profitPaidOut);
  const modalCycleNumber = safeNumber(checkInInvestment?.cycleNumber) || 1;

  const modalGrowthPercentage =
    modalPrincipal > 0 && modalCurrentValue > 0
      ? (modalCurrentValue / modalPrincipal - 1) * 100
      : 0;

  const modalProgressWidth = Math.min(
    100,
    Math.max(0, (modalCompletedDays / 30) * 100)
  );

  const dailyGrowth = lastCheckInResult?.dailyGrowth || null;

  if (loadingMetrics) {
    return (
      <div className="erax-dashboard-wrapper">
        <p className="loading-text">Loading portfolio...</p>
      </div>
    );
  }

  return (
    <div className="erax-dashboard-wrapper">
      <div className="live-indicator investment-info-banner">
        <Gift size={16} />
        <span>
          Perpetual Auto-Renewing Investment • 100% ROI Every 30 Days
        </span>
      </div>

      <div className="erax-stats-strip">
        {portfolioStats.map(stat => (
          <div key={stat.id} className="erax-stat-node">
            <span className="erax-node-label">
              {stat.icon} {stat.label}
            </span>

            <div className="erax-node-value-group">
              <h3>{stat.value}</h3>
              <span
                className={`erax-mini-badge ${stat.isPositive ? 'up' : 'down'}`}
              >
                {stat.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="erax-workspace-panel daily-trading-section">
        <div className="panel-header-action">
          <div>
            <h3 className="panel-title">Daily Trading</h3>
            <p className="panel-subtitle">
              Complete your 24-hour check-ins to keep your investment cycle
              growing
            </p>
          </div>
        </div>

        <div className="holdings-table-container">
          {dailyTradingInvestments.length > 0 ? (
            dailyTradingInvestments.map((inv, index) => {
              const hoursUntilNextCheckIn = getHoursUntilNextCheckIn(inv);
              const canCheckInNow = hoursUntilNextCheckIn === 0;
              const amount = safeNumber(inv.amount);
              const interestAmount = safeNumber(inv.interestAmount);
              const completedDays = safeNumber(inv.completedDays);
              const progressWidth = Math.min(
                100,
                Math.max(0, (completedDays / 30) * 100)
              );

              return (
                <div
                  key={inv.id || `daily-trading-${index}`}
                  className="holding-row-card investment-card"
                >
                  <div
                    className={`holding-icon-box ${
                      inv.assetClass || 'commodities'
                    }-tint`}
                  >
                    {getAssetIcon(inv.assetClass)}
                  </div>

                  <div className="holding-identity-cell">
                    <h4>
                      {inv.symbol || 'Investment'} Daily Cycle
                      {safeNumber(inv.cycleNumber) > 1 && (
                        <span
                          style={{
                            marginLeft: '8px',
                            fontSize: '11px',
                            background: 'rgba(59, 130, 246, 0.2)',
                            color: '#60a5fa',
                            padding: '2px 8px',
                            borderRadius: '4px'
                          }}
                        >
                          Cycle {safeNumber(inv.cycleNumber)}
                        </span>
                      )}
                    </h4>

                    <span>Locked Principal: {formatMoney(amount)}</span>

                    <div className="daily-progress-container">
                      <div className="daily-progress-bar">
                        <div
                          className="daily-progress-fill"
                          style={{ width: `${progressWidth}%` }}
                        ></div>
                      </div>

                      <span className="daily-progress-text">
                        Day {completedDays}/30
                      </span>
                    </div>
                  </div>

                  <div className="holding-data-cell text-right">
                    <span className="cell-top market-value">
                      Target Profit: +{formatMoney(interestAmount)}
                    </span>

                    {getStatusBadge(inv)}

                    {!canCheckInNow && (
                      <span
                        className="text-muted"
                        style={{
                          fontSize: '10px',
                          marginTop: '4px'
                        }}
                      >
                        Next check-in in {hoursUntilNextCheckIn}h
                      </span>
                    )}
                  </div>

                  <div className="investment-actions">
                    <button
                      className={`btn-survey continue ${
                        !canCheckInNow ? 'disabled' : ''
                      }`}
                      onClick={() =>
                        canCheckInNow && handleOpenCheckInModal(inv)
                      }
                      disabled={!canCheckInNow}
                      title={
                        !canCheckInNow
                          ? `Check-in available in ${hoursUntilNextCheckIn} hours`
                          : 'Daily Check-In'
                      }
                    >
                      <Calendar size={14} />
                      {canCheckInNow
                        ? 'Daily Check-In'
                        : `Wait ${hoursUntilNextCheckIn}h`}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-state">
              <p>No daily trading check-ins available today.</p>
              <p
                style={{
                  fontSize: '13px',
                  color: '#94a3b8',
                  marginTop: '8px'
                }}
              >
                Once you have an active investment cycle, your daily
                trading/check-ins will appear here.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="erax-split-workspace">
        <div className="erax-workspace-panel main-ledger-panel">
          <div className="panel-header-action">
            <div>
              <h3 className="panel-title">My Investments</h3>
              <p className="panel-subtitle">
                {safeInvestments.length} total cycles • Auto-renewing every 30
                days
              </p>
            </div>
          </div>

          <div className="holdings-table-container">
            {safeInvestments.length > 0 ? (
              safeInvestments.map((inv, index) => {
                const hoursUntilNextCheckIn = getHoursUntilNextCheckIn(inv);
                const canCheckInNow = hoursUntilNextCheckIn === 0;

                const amount = safeNumber(inv.amount);
                const currentValue = safeNumber(
                  inv.currentValue || inv.amount || 0
                );
                const totalInterest = safeNumber(inv.totalInterestEarned);
                const interestAmount = safeNumber(inv.interestAmount);
                const completedDays = safeNumber(inv.completedDays);
                const profitPaidOut = safeNumber(inv.profitPaidOut);

                const growthPercentage =
                  amount > 0
                    ? (currentValue / amount - 1) * 100
                    : 0;

                const progressWidth = Math.min(
                  100,
                  Math.max(0, (completedDays / 30) * 100)
                );

                return (
                  <div
                    key={inv.id || `investment-${index}`}
                    className="holding-row-card investment-card"
                  >
                    <div
                      className={`holding-icon-box ${
                        inv.assetClass || 'commodities'
                      }-tint`}
                    >
                      {getAssetIcon(inv.assetClass)}
                    </div>

                    <div className="holding-identity-cell">
                      <h4>
                        {inv.symbol || 'Investment'} Investment
                        {safeNumber(inv.cycleNumber) > 1 && (
                          <span
                            style={{
                              marginLeft: '8px',
                              fontSize: '11px',
                              background: 'rgba(59, 130, 246, 0.2)',
                              color: '#60a5fa',
                              padding: '2px 8px',
                              borderRadius: '4px'
                            }}
                          >
                            Cycle {safeNumber(inv.cycleNumber)}
                          </span>
                        )}
                      </h4>

                      <span>Locked Principal: {formatMoney(amount)}</span>

                      {isDailyTradingInvestment(inv) && totalInterest > 0 && (
                        <div
                          style={{
                            marginTop: '8px',
                            padding: '8px',
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            borderRadius: '6px'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <span
                              style={{
                                fontSize: '11px',
                                color: '#94a3b8'
                              }}
                            >
                              Current Value
                            </span>

                            <span
                              style={{
                                fontSize: '14px',
                                fontWeight: '700',
                                color: '#10b981'
                              }}
                            >
                              {formatMoney(currentValue)}
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: '10px',
                              color: '#10b981',
                              marginTop: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <TrendingUp size={10} />
                            +{formatMoney(totalInterest)} earned (
                            {safeNumber(growthPercentage).toFixed(1)}% growth)
                          </div>
                        </div>
                      )}

                      {inv.isAutoRenewed && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginTop: '4px'
                          }}
                        >
                          <RefreshCw size={10} /> Auto-renewed from Cycle{' '}
                          {safeNumber(inv.cycleNumber) - 1}
                        </span>
                      )}

                      {isDailyTradingInvestment(inv) && (
                        <div className="daily-progress-container">
                          <div className="daily-progress-bar">
                            <div
                              className="daily-progress-fill"
                              style={{ width: `${progressWidth}%` }}
                            ></div>
                          </div>

                          <span className="daily-progress-text">
                            Day {completedDays}/30
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="holding-data-cell text-right">
                      <span className="cell-top market-value">
                        Profit: +{formatMoney(interestAmount)}
                      </span>

                      {getStatusBadge(inv)}

                      {!canCheckInNow && isDailyTradingInvestment(inv) && (
                        <span
                          className="text-muted"
                          style={{
                            fontSize: '10px',
                            marginTop: '4px'
                          }}
                        >
                          Next check-in in {hoursUntilNextCheckIn}h
                        </span>
                      )}
                    </div>

                    <div className="investment-actions">
                      {isDailyTradingInvestment(inv) && (
                        <button
                          className={`btn-survey continue ${
                            !canCheckInNow ? 'disabled' : ''
                          }`}
                          onClick={() =>
                            canCheckInNow && handleOpenCheckInModal(inv)
                          }
                          disabled={!canCheckInNow}
                          title={
                            !canCheckInNow
                              ? `Check-in available in ${hoursUntilNextCheckIn} hours`
                              : 'Daily Check-In'
                          }
                        >
                          <Calendar size={14} />
                          {canCheckInNow
                            ? 'Daily Check-In'
                            : `Wait ${hoursUntilNextCheckIn}h`}
                        </button>
                      )}

                      {(inv.interestStatus === 'claimed' ||
                        (inv.status === 'auto_renewed' &&
                          !isDailyTradingInvestment(inv))) && (
                        <span className="claimed-text">
                          <CheckCircle2 size={14} />
                          {profitPaidOut > 0
                            ? `${formatMoney(profitPaidOut)} Paid`
                            : 'Complete'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state">
                <p>No active investments yet.</p>
                <p
                  style={{
                    fontSize: '13px',
                    color: '#94a3b8',
                    marginTop: '8px'
                  }}
                >
                  Make a deposit to start your first investment cycle!
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="analytics-side-panel">
          <div className="inner-card how-it-works-card">
            <h4 className="card-title">
              <Trophy size={16} /> How It Works
            </h4>

            <div className="steps-list">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Deposit & Auto-Invest</strong>
                  <span>
                    Your deposit is automatically locked and invested for 30
                    days
                  </span>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>Daily Check-In</strong>
                  <span>
                    Enter the 8-character code emailed to you every 24 hours
                  </span>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>Watch Your Investment Grow</strong>
                  <span>
                    Earn 3.33% daily interest - your investment grows every day!
                  </span>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">4</div>
                <div className="step-content">
                  <strong>Profit Released & Auto-Renew</strong>
                  <span>
                    100% profit is paid out, principal stays locked and
                    auto-renews forever
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: '20px',
                padding: '16px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#94a3b8',
                lineHeight: '1.6'
              }}
            >
              <strong
                style={{
                  color: '#60a5fa',
                  display: 'block',
                  marginBottom: '8px'
                }}
              >
                💡 Perpetual Investment Model
              </strong>
              Your locked principal earns 3.33% daily interest (100% total over
              30 days). Watch your investment grow in real-time! At day 30,
              profits are paid out and your principal auto-renews for the next
              cycle.
            </div>
          </div>

          <div className="inner-card chat-card">
            <h4 className="card-title">
              <MessageSquare size={16} /> Community
            </h4>

            <div className="chat-messages">
              {messages.map(msg => {
                const isMe = currentUser && msg.uid === currentUser.uid;

                return (
                  <div key={msg.id} className={`chat-msg ${isMe ? 'me' : ''}`}>
                    <span className="chat-name">{msg.userName}</span>
                    <p className="chat-text">{msg.text}</p>
                  </div>
                );
              })}

              <div ref={chatEndRef} />
            </div>

            {currentUser && (
              <form onSubmit={handleSendMessage} className="chat-form">
                <input
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Share insights..."
                  disabled={sending}
                />

                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                >
                  {sending ? <Clock size={14} /> : <Send size={14} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {showCheckInModal && checkInInvestment && (
        <div
          className="modal-overlay"
          onClick={() => !checkInLoading && setShowCheckInModal(false)}
        >
          <div
            className="modal-content claim-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Daily Check-In</h3>

              <button
                className="modal-close"
                onClick={() => setShowCheckInModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="claim-modal-body">
              {modalCycleNumber > 1 && (
                <div
                  style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#60a5fa',
                    textAlign: 'center'
                  }}
                >
                  🔄 Cycle {modalCycleNumber} - Auto-Renewed Investment
                </div>
              )}

              <div className="claim-info-box">
                <div className="info-row">
                  <span>Current Streak:</span>
                  <strong className="text-gold">
                    Day {modalCompletedDays} of 30
                  </strong>
                </div>

                <div className="info-row">
                  <span>Locked Principal:</span>
                  <strong>{formatMoney(modalPrincipal)}</strong>
                </div>

                {modalCurrentValue > modalPrincipal && modalPrincipal > 0 && (
                  <div className="info-row">
                    <span>Current Value:</span>
                    <strong style={{ color: '#10b981' }}>
                      {formatMoney(modalCurrentValue)}
                      <span
                        style={{
                          fontSize: '10px',
                          marginLeft: '4px'
                        }}
                      >
                        (+{safeNumber(modalGrowthPercentage).toFixed(1)}%)
                      </span>
                    </strong>
                  </div>
                )}

                <div className="info-row">
                  <span>Target Profit:</span>
                  <strong className="text-gold">
                    +{formatMoney(modalTargetProfit)}
                  </strong>
                </div>

                {modalProfitPaidOut > 0 && (
                  <div className="info-row">
                    <span>Previous Profits:</span>
                    <strong style={{ color: '#10b981' }}>
                      {formatMoney(modalProfitPaidOut)}
                    </strong>
                  </div>
                )}
              </div>

              <div
                className="daily-progress-container"
                style={{ marginBottom: '24px' }}
              >
                <div
                  className="daily-progress-bar"
                  style={{ height: '10px' }}
                >
                  <div
                    className="daily-progress-fill"
                    style={{ width: `${modalProgressWidth}%` }}
                  ></div>
                </div>

                <span className="daily-progress-text">
                  {Math.round(modalProgressWidth)}%
                </span>
              </div>

              {dailyGrowth && (
                <div
                  style={{
                    marginTop: '16px',
                    marginBottom: '16px',
                    padding: '16px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '12px',
                    animation: 'fadeIn 0.5s ease-in'
                  }}
                >
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#10b981',
                      fontWeight: '700',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <TrendingUp size={18} />
                    Daily Growth Update
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '12px',
                      color: '#94a3b8',
                      marginBottom: '6px'
                    }}
                  >
                    <span>Today's Interest:</span>
                    <strong style={{ color: '#10b981' }}>
                      +{formatMoney(dailyGrowth.dailyInterestEarned)}
                    </strong>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '12px',
                      color: '#94a3b8',
                      marginBottom: '6px'
                    }}
                  >
                    <span>Total Earned:</span>
                    <strong style={{ color: '#10b981' }}>
                      +{formatMoney(dailyGrowth.totalInterestEarned)}
                    </strong>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '12px',
                      color: '#94a3b8',
                      marginBottom: '6px'
                    }}
                  >
                    <span>Current Value:</span>
                    <strong style={{ color: '#10b981' }}>
                      {formatMoney(dailyGrowth.currentValue)}
                    </strong>
                  </div>

                  <div
                    style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(16, 185, 129, 0.2)',
                      fontSize: '11px',
                      color: '#10b981',
                      textAlign: 'center',
                      fontWeight: '600'
                    }}
                  >
                    📈 {safeNumber(dailyGrowth.growthPercentage).toFixed(1)}%
                    Growth
                  </div>
                </div>
              )}

              {!lastCheckInResult && (
                <div className="claim-input-section">
                  <label>Enter Today's 8-Character Code:</label>

                  <p
                    style={{
                      fontSize: '12px',
                      color: '#94a3b8',
                      marginBottom: '12px'
                    }}
                  >
                    Check your email for today's unique code.
                  </p>

                  <input
                    type="text"
                    value={checkInCode}
                    onChange={e => setCheckInCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXXXX"
                    maxLength={8}
                    className="claim-code-input"
                    disabled={checkInLoading}
                  />

                  {checkInError && (
                    <div className="claim-error">
                      <AlertCircle size={14} />
                      <span>{checkInError}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              {!lastCheckInResult && (
                <>
                  <button
                    className="btn-secondary"
                    onClick={() => setShowCheckInModal(false)}
                    disabled={checkInLoading}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn-primary"
                    onClick={handleDailyCheckIn}
                    disabled={
                      checkInLoading || !checkInCode || checkInCode.length !== 8
                    }
                  >
                    {checkInLoading ? (
                      <>
                        <RefreshCw size={16} className="spin" /> Checking In...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={16} />
                        Check In (Day {modalCompletedDays + 1})
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Investments;